"use client";

import { Activity, BatteryCharging, Cpu, Gauge, Route, Wrench } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Step = {
  number: string;
  duration: string;
  title: string;
  description: string;
  icon: React.ElementType;
};
const steps: Step[] = [{
  number: "01",
  duration: "FASE 01 / 30 MIN",
  title: "ANAMNESI CLINICA",
  description: "Un'analisi approfondita della storia clinica e dei sintomi per inquadrare con precisione il quadro diagnostico iniziale.",
  icon: Activity
}, {
  number: "02",
  duration: "FASE 02 / 45 MIN",
  title: "VALUTAZIONE STRUMENTALE",
  description: "Esami ecografici di alta precisione per monitorare la fisiopatologia della riproduzione e lo stato di salute ostetrico.",
  icon: BatteryCharging
}, {
  number: "03",
  duration: "FASE 03 / 60 MIN",
  title: "CONSULENZA SPECIALISTICA",
  description: "Discussione dettagliata dei risultati e pianificazione di un percorso terapeutico personalizzato basato su evidenze scientifiche.",
  icon: Wrench
}, {
  number: "04",
  duration: "FASE 04 / 30 MIN",
  title: "VALUTAZIONE FORENSE",
  description: "Analisi tecnica della documentazione medica per perizie di parte o consulenze in ambito di ginecologia forense.",
  icon: Cpu
}, {
  number: "04",
  duration: "FASE 05 / 40 MIN",
  title: "PIANIFICAZIONE TERAPEUTICA",
  description: "Definizione dei protocolli di cura, monitoraggio costante e adattamento del piano in base alla risposta clinica.",
  icon: Gauge
}, {
  number: "06",
  duration: "FASE 06 / 20 MIN",
  title: "FOLLOW-UP E CONTROLLO",
  description: "Verifica periodica dei progressi clinici per garantire l'efficacia del trattamento e il benessere della paziente.",
  icon: Route
}];
function TimelineStep({
  step,
  index
}: {
  step: Step;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  const isLeft = index % 2 === 0;
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-20 pl-16 md:pl-0">
      <div className={`absolute left-[22px] md:hidden top-2 w-6 h-6 transition-all duration-700 ${isVisible ? "bg-rose-400" : "bg-slate-900"}`} style={{
      transform: "translateX(-50%)"
    }} />

      <div className={`hidden md:block absolute left-1/2 top-8 w-6 h-6 z-10 transition-all duration-700 ${isVisible ? "bg-rose-400" : "bg-slate-900"}`} style={{
      transform: "translate(-50%, 0)"
    }} />

      <div className={`${isLeft ? "md:block" : "md:hidden"} md:pr-12 lg:pr-16`}>
        {isLeft && <div className={`flex flex-col md:items-end transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="font-default font-black leading-none mb-6 md:text-right" style={{
          fontSize: "clamp(72px, 12vw, 144px)",
          color: "transparent",
          WebkitTextStroke: "2px hsl(350 70% 65% / 0.3)",
          letterSpacing: "-0.05em"
        }}>
              {step.number}
            </div>
            <StepCard step={step} Icon={Icon} />
          </div>}
      </div>

      <div className={`${!isLeft ? "md:block md:col-start-2" : "md:hidden"} md:pl-12 lg:pl-16`}>
        {!isLeft && <div className={`flex flex-col transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="font-default font-black leading-none mb-6" style={{
          fontSize: "clamp(72px, 12vw, 144px)",
          color: "transparent",
          WebkitTextStroke: "2px hsl(350 70% 65% / 0.3)",
          letterSpacing: "-0.05em"
        }}>
              {step.number}
            </div>
            <StepCard step={step} Icon={Icon} />
          </div>}
      </div>
    </div>;
}
function StepCard({
  step,
  Icon
}: {
  step: Step;
  Icon: React.ElementType;
}) {
  return <div className="relative p-8 border border-slate-200 backdrop-blur-sm w-full bg-white shadow-lg">
      <div className="absolute top-6 right-6">
        <Icon className="w-6 h-6 text-rose-500" strokeWidth={1.5} />
      </div>

      <div className="font-mono text-xs uppercase tracking-widest mb-4 pr-10 text-rose-600">
        {step.duration}
      </div>

      <h3 className="font-default font-bold uppercase mb-5 leading-tight text-slate-900" style={{
      fontSize: "clamp(22px, 3vw, 32px)",
      letterSpacing: "-0.02em"
    }}>
        {step.title}
      </h3>

      <p className="font-default text-base leading-relaxed text-slate-700">
        {step.description}
      </p>
    </div>;
}
export default function Percorso() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="2539" id="il-percorso-di-cura" className="relative overflow-hidden px-6 md:px-12 lg:px-20 bg-slate-50 text-slate-900" style={{
    paddingTop: "160px",
    paddingBottom: "160px"
  }}>
      <div ref={headerRef} className={`max-w-7xl mx-auto mb-24 md:mb-32 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="font-mono text-xs uppercase tracking-widest mb-6 text-rose-600">
          § 01 // PERCORSO
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-end">
          <h2 className="font-default font-bold uppercase leading-[0.9] text-slate-950" style={{
          fontSize: "clamp(48px, 9vw, 80px)",
          letterSpacing: "-0.04em"
        }}>
            IL PERCORSO
            <br />
            DI CURA.
          </h2>

          <p className="font-default text-lg md:text-xl leading-relaxed text-slate-700">
            Un approccio multidisciplinare che unisce rigore scientifico e sensibilità umana. Ogni fase del percorso è studiata per garantire la massima precisione diagnostica e terapeutica.
          </p>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="absolute left-[22px] md:left-1/2 top-0 bottom-0 w-[1px] bg-slate-200" style={{
        transform: "translateX(-0.5px)"
      }} />

        <div className="flex flex-col gap-24 md:gap-32 relative">
          {steps.map((step, i) => <TimelineStep key={i} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}